import React, {useState} from 'react';

import {Stack, Table, Text, ActionIcon, Group} from '@mantine/core';
import {IconTrash, IconCheck, IconX} from '@tabler/icons-react';

import {useAction, useSelectorWithRefresh} from '@/store/kysely_module';
import {formatTimeAgo} from '@/components/new_dashboard/utils/timeFormatting';

type Props = {
    workspace_id: string;
    isAdmin: boolean;
};

export const WorkspaceMembersForm = (props: Props) => {
    const [members, refreshMembers] = useSelectorWithRefresh('getWorkspaceMembers', {workspace_id: props.workspace_id}, [], {workspace_id: props.workspace_id});
    const removeWorkspaceMember = useAction('removeWorkspaceMember');
    const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);

    const handleRemoveMember = async (userId: string) => {
        try {
            await removeWorkspaceMember({
                workspace_id: props.workspace_id,
                user_id: userId,
            }, {workspace_id: props.workspace_id});
            setConfirmingUserId(null);
            refreshMembers();
        } catch (error) {
            console.error('Failed to remove member', error);
            setConfirmingUserId(null);
        }
    };

    if (members.length === 0) {
        return (
            <Stack align="center" py="xl">
                <Text c="dimmed">No members found</Text>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Table striped highlightOnHover withRowBorders>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Role</Table.Th>
                        <Table.Th>Joined</Table.Th>
                        {props.isAdmin && <Table.Th style={{width: '80px'}}></Table.Th>}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {members.map((member) => {
                        const isOwner = member.user_id === member.owner_id;
                        const isConfirming = confirmingUserId === member.user_id;
                        const displayRole = isOwner ? 'Owner' : 'Member';
                        return (
                            <Table.Tr key={member.id}>
                                <Table.Td>
                                    <Text size="sm">{member.name}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm" c="dimmed">{displayRole}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm" c="dimmed">
                                        {formatTimeAgo(member.created_at, false)}
                                    </Text>
                                </Table.Td>
                                {props.isAdmin && (
                                    <Table.Td>
                                        {!isOwner && (
                                            <Group gap="xs" wrap="nowrap">
                                                {isConfirming ? (
                                                    <>
                                                        <ActionIcon
                                                            variant="filled"
                                                            color="red"
                                                            size="sm"
                                                            onClick={() => handleRemoveMember(member.user_id)}
                                                        >
                                                            <IconCheck size={14} />
                                                        </ActionIcon>
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray"
                                                            size="sm"
                                                            onClick={() => setConfirmingUserId(null)}
                                                        >
                                                            <IconX size={14} />
                                                        </ActionIcon>
                                                    </>
                                                ) : (
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        onClick={() => setConfirmingUserId(member.user_id)}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                )}
                                            </Group>
                                        )}
                                    </Table.Td>
                                )}
                            </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
        </Stack>
    );
};
